import {
    Separator,
    Text
} from "@fluentui/react";
import { RootState } from "app/store";
import SimpleCard, { ECalloutTypes, ECardIcon, ICardProperties } from "components/controls/SimpleCard";
import { appStyles } from "global/Application";
import {
    bcbs_config_category as IXrmTypes_bcbs_config_category
} from "model/CRM";
import { IBenefitList } from "model/CRM.type";
import {
    IBenefit as FAM_IBenefit,
    IPlan as FAM_IPlan
} from "model/FlexibleAttributeModel";
import React from "react";
import BenefitCallout from "./Controls/BenefitCallout";

export enum EBenefitCardFunction {
    display,
    addToPlan,
    addToGroupConfiguration,
}
export interface IProps extends ICardProperties {
    benefit: FAM_IBenefit;
    benefitCollection?: IBenefitList;
    contextPlans?: FAM_IPlan[];
    currentPlan?: FAM_IPlan | null;
    currentPlanBenefits?: FAM_IBenefit[] | null;
    currentPlanCategories?: IXrmTypes_bcbs_config_category[] | null;
    state: RootState
    mode: EBenefitCardFunction
    icon?: ECardIcon;
}

const redStyle = { root: { color: 'red' } };


/**
 * 
 * Note: this component has two display modes:
 * - display for showing within collection
 * - addition to allow adding to collections, which applies some of the rules and validations by disabling
 * @param props 
 * @returns 
 */
const BenefitCard: React.FC<IProps> = (props) => {

    const onRenderAdd = () => {

        const {
            benefit,
            onClick,
            benefitCollection,
            currentPlan,
            currentPlanBenefits,
            currentPlanCategories,
        } = props;

        const planHasThisBenefit = currentPlanBenefits?.some(q => q.id === benefit.id)

        const planHasThisCategory = currentPlanCategories?.some(q => q.bcbs_config_categoryid === benefit.bcbs_config_categoryid);

        const currentPlanSuperCategories = currentPlanCategories?.map(t => props.state.data.config.cache.map_bcbs_config_supercategory_by_bcbs_config_categoryid[t.bcbs_config_categoryid!]) ?? [];

        // _bcbs_supercategoryid_value might not be available in the benefit object, since it was introduced later

        const planHasThisSuperCategory = currentPlanSuperCategories?.some(q => q.bcbs_config_supercategoryid === benefit._bcbs_supercategoryid_value);

        const similar = benefitCollection?.some(t =>

            // selected ?
            t.selected &&

            // same category or super category
            (t.item.bcbs_config_categoryid === benefit.bcbs_config_categoryid || t.item._bcbs_supercategoryid_value === benefit._bcbs_supercategoryid_value) &&

            // not the same benefit
            t.item.id !== benefit.id);


        let fundingTypeMissMatch = props.currentPlan?.fundingType?.value !== benefit.fundingType?.value;

        if (!benefit.bcbs_isfundingtypeenabled)
            fundingTypeMissMatch = false;

        const grandFatheredMissMatch = benefit.bcbs_isgrandfatherenabled && currentPlan?.grandFathered?.value !== benefit.grandFathered?.value;

        const disabled = planHasThisBenefit || planHasThisCategory || similar || grandFatheredMissMatch || fundingTypeMissMatch || planHasThisSuperCategory;

        const showCallout = disabled;

        const selected = benefitCollection?.some(t => t.selected && benefit.id === t.item.id);

        const onCallout = () => {
            return (
                <>
                    <BenefitCallout
                        contextPlans={props.contextPlans}
                        benefit={props.benefit}
                        mode={props.mode}
                    />
                    {showCallout &&
                        <>
                            <Separator />
                            <Text block variant="xLarge" className={appStyles.title}>
                                Notifications
                            </Text>
                            {planHasThisBenefit &&
                                <>
                                    <Text block variant="small" styles={redStyle}>This benefit has already been selected.</Text>
                                    <Separator />
                                </>
                            }
                            {planHasThisCategory &&
                                <>
                                    <Text block variant="small" styles={redStyle}>A benefit with same category has already been associated with current context.</Text>
                                    <Separator />
                                </>
                            }
                            {planHasThisSuperCategory &&
                                <>
                                    <Text block variant="small" styles={redStyle}>A benefit with same s-category has already been associated with current context.</Text>
                                    <Separator />
                                </>
                            }
                            {similar &&
                                <>
                                    <Text block variant="small" styles={redStyle}>A benefit with same category has already been selected.</Text>
                                    <Separator />
                                </>
                            }
                            {fundingTypeMissMatch &&
                                <>
                                    <Text block variant="small" styles={redStyle}>Benefit's funding ({benefit.fundingType?.name}) does not match plans funding ({currentPlan?.fundingType?.name}).</Text>
                                    <Separator />
                                </>
                            }
                            {grandFatheredMissMatch &&
                                <>
                                    <Text block variant="small" styles={redStyle}>Benefit's GF/NGF type ({benefit.grandFathered?.name}) does not match plan's GF/NGF type ({currentPlan?.grandFathered?.name}).</Text>
                                    <Separator />
                                </>
                            }
                        </>
                    }
                    {/* 
                    <Text block variant="xLarge" className={appStyles.title}>
                        Benefit Information
                    </Text>
                    <Text block variant="small" >
                        Individual deductible $0.00<br />
                        Family deductible $0.00<br />
                        Out of pocket max $0.00<br />
                    </Text> */}
                </>
            );
        }

        return (
            <SimpleCard
                {...props}
                key={benefit.id}
                description={benefit.name}
                selected={selected}
                header={benefit.bcbs_name}
                disabled={disabled}
                icons={props.icon}
                onCallout={onCallout}
                callout={showCallout ? ECalloutTypes.notification : ECalloutTypes.comment}
                onClick={onClick}
                overflowItems={props.overflowItems}
            // deactivated={benefit.deactivated}
            />
        );
    }

    const onRenderDisplay = () => {
        const { benefit, benefitCollection, onClick } = props;
        const selected = benefitCollection?.some(t => t.selected && benefit.id === t.item.id);

        const onCallout = () => {
            return <BenefitCallout
                contextPlans={props.contextPlans}
                benefit={props.benefit}
                mode={props.mode}
            />
        }

        let header = benefit.bcbs_name;

        // Check if this benefit is from Association Template
        const isFromAssociationTemplate = benefit.bcbs_fromassociationtemplate === true;

        return (
            <SimpleCard
                {...props}
                key={benefit.id}
                description={benefit.name}
                selected={selected}
                header={header}
                icons={isFromAssociationTemplate ? ECardIcon.template : props.icon}
                onCallout={onCallout}
                onClick={onClick}
                overflowItems={props.overflowItems}
            // deactivated={benefit.deactivated}
            />
        )
    }

    if (props.mode === EBenefitCardFunction.addToPlan)
        return onRenderAdd();

    return onRenderDisplay();

}

export default BenefitCard;